import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Play, Clock, Lock, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';

const CategoryPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const [category, setCategory] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [catRes, vidRes] = await Promise.all([
          axios.get(`${API}/categories/${slug}?locale=${i18n.language}`),
          axios.get(`${API}/videos?locale=${i18n.language}`, { headers })
        ]);
        
        setCategory(catRes.data);
        // Filter videos by category
        const categoryVideos = vidRes.data.filter(v => v.category_id === catRes.data.id);
        setVideos(categoryVideos);
      } catch (e) {
        console.error('Failed to fetch data', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, i18n.language, token]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} ${t('common.min')}`;
  };

  const categoryImages = {
    cutting: 'https://images.pexels.com/photos/15659458/pexels-photo-15659458.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    color: 'https://images.pexels.com/photos/7388933/pexels-photo-7388933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    styling: 'https://images.pexels.com/photos/14615064/pexels-photo-14615064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-misel-bg">
        <Navbar />
        <div className="pt-24 px-8 md:px-16">
          <div className="animate-pulse">
            <div className="h-64 bg-misel-card rounded-sm mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-video bg-misel-card rounded-sm" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-misel-bg">
        <Navbar />
        <div className="pt-24 px-8 md:px-16 text-center">
          <h1 className="font-roboto font-bold text-2xl text-white">Category not found</h1>
        </div>
      </div>
    );
  }

  const heroImageUrl = category.hero_image_url
    || category.banner_image_url
    || category.image_url
    || categoryImages[category.slug]
    || categoryImages.cutting;

  return (
    <div className="min-h-screen bg-misel-bg">
      <Navbar />
      
      {/* Hero Image */}
      <section className="category-hero-image pt-20 md:pt-24">
        <div className="relative flex w-full h-[320px] md:h-[420px] lg:h-[600px] items-center justify-center overflow-hidden bg-black">
          <img
            src={heroImageUrl}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full scale-105 object-cover object-center opacity-40 blur-md"
          />
          <div className="pointer-events-none absolute inset-0 bg-black/25" />
          <img
            src={heroImageUrl}
            alt={category.title}
            className="relative z-10 max-h-full max-w-full object-contain object-center"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-1/3 bg-gradient-to-t from-misel-bg to-transparent" />
        </div>
      </section>

      <main className="px-8 md:px-16">
        {/* Category Content */}
        <section className="category-copy pt-8 md:pt-10">
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-5 transition-colors"
          >
            <ChevronLeft size={20} />
            {t('categories.title')}
          </Link>
          <h1 className="font-roboto font-bold text-3xl md:text-5xl text-white tracking-wide">
            {category.title}
          </h1>
          <p className="text-gray-400 mt-4 max-w-3xl leading-relaxed">
            {category.description}
          </p>
        </section>

        {/* Videos Grid */}
        <section className="videos-grid py-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-roboto font-bold text-xl text-white">
            {videos.length} {i18n.language === 'el' ? 'Βίντεο' : 'Videos'}
          </h2>
        </div>

        {videos.length === 0 ? (
          <p className="text-gray-500 text-center py-12">
            {i18n.language === 'el' ? 'Δεν υπάρχουν διαθέσιμα βίντεο' : 'No videos available'}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Link
                  to={`/video/${video.slug}`}
                  data-testid={`video-${video.slug}`}
                  className="block group"
                >
                  <div className="aspect-video relative overflow-hidden rounded-sm card-scale bg-misel-card border border-white/5">
                    <div
                      className="pointer-events-none absolute inset-0 bg-cover bg-center"
                      style={{
                        backgroundImage: video.thumbnail_url
                          ? `url('${video.thumbnail_url}')`
                          : `url('${categoryImages[category.slug] || categoryImages.cutting}')`
                      }}
                    />
                    <div className="pointer-events-none absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
                    
                    {/* Play button */}
                    {video.has_access && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-14 h-14 rounded-full bg-primary/90 flex items-center justify-center">
                          <Play size={24} className="text-white ml-1" fill="white" />
                        </div>
                      </div>
                    )}
                    
                    {/* Duration */}
                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 rounded-sm text-xs text-white flex items-center gap-1">
                      <Clock size={12} />
                      {formatDuration(video.duration_seconds)}
                    </div>
                    
                    {/* Lock */}
                    {!video.has_access && (
                      <div className="absolute top-2 right-2">
                        <Lock size={16} className="text-white/70" />
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-3">
                    <h4 className="text-white font-medium line-clamp-2 group-hover:text-primary transition-colors">
                      {video.title}
                    </h4>
                    <p className="text-gray-500 text-sm mt-1 line-clamp-2">
                      {video.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
      </main>
    </div>
  );
};

export default CategoryPage;
